import {
  Link,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '@/components/shared/ProgressBar';
import AgentsSelection from '@/features/appointments/components/AgentsSelection';
import useAgentsAndTimeslots from '@/features/scrollFlow/pages/Calendar/hooks/useAgentsAndTimeslots';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import useProgress from '@/hooks/useProgress';
import { Page } from '@/lib/tagging';
import { mapCommPref } from '@/lib/tagging/commPrefMapping';
import useTagging from '@/lib/tagging/useTagging';
import { selectTrvEndLocationForTagging } from '@/lib/tagging/utils';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';

type AgentSelectionLayoutProps = {
  continueButtonDisabled?: boolean;
  isRescheduleFlow?: boolean;
  onClickContinue: () => void;
};

const AgentSelectionLayout: React.FC<AgentSelectionLayoutProps> = ({
  continueButtonDisabled = false,
  isRescheduleFlow = false,
  onClickContinue,
}) => {
  const tagging = useTagging();
  const tagged = useRef(false);

  const selectedAppointmentType = useAppointmentStore(
    (state) => state.selectedAppointmentType,
  );
  const { agentsForSelectedTimeSlot, isLoading: isLoadingAgents } =
    useAgentsAndTimeslots();

  const selectedAgent = useAppointmentStore((state) => state.selectedAgent);
  const setSelectedAgent = useAppointmentStore(
    (state) => state.setSelectedAgent,
  );
  const trvEndLocation = useAppointmentStore(selectTrvEndLocationForTagging);
  const selectedStartTime = useAppointmentStore(
    (state) => state.selectedStartTime,
  );
  const contactInfo = useAppointmentStore((state) => state.contactInfo);
  const { sessionId, sessionSource } = useSessionStore();

  useLandingPageRedirection();
  useProgress(60);

  const navigate = useNavigate();

  const isInBranchAppointment = selectedAppointmentType === 'in-branch';
  const isOnlineAppointment = selectedAppointmentType === 'online';
  const isButtonDisabled =
    continueButtonDisabled || isLoadingAgents || !selectedAgent?.agentRecordId;

  const start = selectedStartTime ? dayjs(selectedStartTime) : null;
  const heading = start
    ? `Available travel advisors on ${start.format('MMMM D')} at ${start.format('hh:mma')}`
    : 'Available travel advisors';
  const showEmptyState =
    agentsForSelectedTimeSlot.length === 0 && !isLoadingAgents;

  useEffect(() => {
    if (!tagged.current) {
      tagging.tag('view', sessionSource, {
        page: Page.selectAgent,
        sessionid: sessionId,
        flow: isRescheduleFlow ? 'modify' : '',
        trv_end_location: trvEndLocation,
        comm_pref: mapCommPref(
          contactInfo.communicationPreference,
          isInBranchAppointment,
          isOnlineAppointment,
        ),
      });
      tagged.current = true;
    }
  }, [
    sessionSource,
    sessionId,
    tagging.tag,
    contactInfo.communicationPreference,
    isInBranchAppointment,
    isOnlineAppointment,
    trvEndLocation,
    isRescheduleFlow,
  ]);

  const handleClickBack = () => {
    navigate(-1);
  };

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: 'background.default',
      }}
    >
      <Stack
        spacing={4}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          px: 2,
          pt: 4,
          overflow: 'hidden',
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: '100%',
            maxWidth: '600px',
            textAlign: 'left',
            minHeight: 0,
          }}
        >
          <Typography variant='h5'>{heading}</Typography>
          <Stack sx={{ flex: 1, minHeight: 0 }}>
            {showEmptyState ? (
              <Typography variant='body1'>
                No nearby agents are available for the selected time slot.
                Please try{' '}
                <Link onClick={handleClickBack}>
                  selecting a different time slot
                </Link>{' '}
                or adjust your location search criteria.
              </Typography>
            ) : (
              <AgentsSelection
                agents={agentsForSelectedTimeSlot}
                selectedAgent={selectedAgent}
                onSelectAgent={setSelectedAgent}
                isLoading={isLoadingAgents}
              />
            )}
          </Stack>
        </Stack>
      </Stack>

      <ProgressBar
        buttonDisabled={isButtonDisabled}
        displayBackButton={true}
        onClickContinue={onClickContinue}
      />
    </Stack>
  );
};

export default AgentSelectionLayout;
